import { Cart, DoctorAddress } from 'types';
import { ITeroCheckoutStates, ITeroOrderStepMode } from '.';
import { useI18n } from 'contexts/i18n';

export const ORDER_STEP_MODES: ITeroOrderStepMode[] = [
  {
    step: 'address',
    modes: {
      address: 'create',
      scannerPayment: 'start',
      subscriptionPayment: 'start',
      rentalPayment: 'start',
      additionalInfo: 'start',
      termsAndConditions: 'start',
    },
  },

  {
    step: 'scannerPayment',
    modes: {
      address: 'edit',
      scannerPayment: 'create',
      subscriptionPayment: 'start',
      rentalPayment: 'start',
      additionalInfo: 'start',
      termsAndConditions: 'start',
    },
  },
  {
    step: 'subscriptionPayment',
    modes: {
      address: 'edit',
      scannerPayment: 'edit',
      subscriptionPayment: 'create',
      rentalPayment: 'start',
      additionalInfo: 'start',
      termsAndConditions: 'start',
    },
  },
  {
    step: 'rentalPayment',
    modes: {
      address: 'edit',
      scannerPayment: 'start',
      subscriptionPayment: 'start',
      rentalPayment: 'create',
      additionalInfo: 'start',
      termsAndConditions: 'start',
    },
  },
  {
    step: 'additionalInfo',
    modes: {
      address: 'edit',
      scannerPayment: 'edit',
      subscriptionPayment: 'edit',
      rentalPayment: 'edit',
      additionalInfo: 'create',
      termsAndConditions: 'start',
    },
  },
  {
    step: 'termsAndConditions',
    modes: {
      address: 'edit',
      scannerPayment: 'edit',
      subscriptionPayment: 'edit',
      rentalPayment: 'edit',
      additionalInfo: 'edit',
      termsAndConditions: 'create',
    },
  },
  {
    step: 'failed',
    modes: {
      address: 'edit',
      scannerPayment: 'edit',
      subscriptionPayment: 'edit',
      rentalPayment: 'edit',
      additionalInfo: 'edit',
      termsAndConditions: 'create',
    },
  },
  {
    step: 'complete',
    modes: {
      address: 'edit',
      scannerPayment: 'edit',
      subscriptionPayment: 'edit',
      rentalPayment: 'edit',
      additionalInfo: 'edit',
      termsAndConditions: 'create',
    },
  },
];

export const getOrderStepModes = (step: ITeroCheckoutStates) => {
  const stepModes = ORDER_STEP_MODES.find(orderStep => orderStep.step === step);
  return stepModes ? stepModes.modes : ORDER_STEP_MODES[0].modes;
};

export const getOrderStepLabel = (
  currentStep: ITeroCheckoutStates,
  isRental?: boolean
) => {
  const i18n = useI18n();
  if (isRental) {
    if (currentStep === 'address') {
      return `1.${i18n['Ship to & Bill to']}`;
    }
    if (currentStep === 'rentalPayment') {
      return `2.${i18n['Rental Service Plan']}`;
    }
    if (currentStep === 'additionalInfo') {
      return `3.${i18n['Additional Details']}`;
    }
    if (currentStep === 'termsAndConditions') {
      return `4.${i18n['Terms & Conditions']}`;
    }
  } else {
    if (currentStep === 'address') {
      return `1.${i18n['Ship to & Bill to']}`;
    }
    if (currentStep === 'scannerPayment') {
      return `2.${i18n['Payment Method']}`;
    }
    if (currentStep === 'subscriptionPayment') {
      return `3.${i18n['Service Plan Payment Method']}`;
    }
    if (currentStep === 'additionalInfo') {
      return `4.${i18n['Additional Details']}`;
    }
    if (currentStep === 'termsAndConditions') {
      return `5.${i18n['Terms & Conditions']}`;
    }
  }
  return '';
};

export const getSelectedShippingAddress = (
  currentOrder: Cart | undefined,
  doctorAddress: DoctorAddress
) => {
  if (currentOrder) {
    const currentOrderShippingAddressId = currentOrder.shipToAddressId;

    if (currentOrderShippingAddressId) {
      const selectedShippingAddress = doctorAddress.shippingAddresses.filter(
        address => address.id === currentOrderShippingAddressId
      )[0];
      return selectedShippingAddress;
    }
  }
  return doctorAddress.defaultShippingAddress;
};

export const getSelectedBillingAddress = (
  currentOrder: Cart | undefined,
  doctorAddress: DoctorAddress
) => {
  if (currentOrder) {
    const currentOrderBillingAddressId = currentOrder.billToAddressId;
    if (currentOrderBillingAddressId) {
      const selectedBillingAddress = doctorAddress.billingAddresses.filter(
        address => address.id === currentOrderBillingAddressId
      )[0];
      return selectedBillingAddress;
    }
  }
  return doctorAddress.defaultBillingAddress;
};

export const isValidEmailAddress = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  )
    ? true
    : false;
};

/**
 * Checks if at least one of the license-related fields (`licenceNumber` or `stateOfIssuance`) is available.
 *
 * @param {string} [licenceNumber] - The license number of the doctor (optional).
 * @param {string} [stateOfIssuance] - The state where the license was issued (optional).
 * @returns {boolean} - Returns `true` if either `licenceNumber` or `stateOfIssuance` is defined, otherwise `false`.
 */
export const isLicenseFieldAvailable = (
  licenceNumber?: string,
  stateOfIssuance?: string
): boolean => {
  return (
    typeof licenceNumber !== 'undefined' ||
    typeof stateOfIssuance !== 'undefined'
  );
};

export const isDeliveryFieldVisible = (deliveryDate?: string): boolean => {
  return typeof deliveryDate !== 'undefined';
};
